import { CreateElement } from 'vue'
import { RouteConfig, Route, NavigationGuardNext } from 'vue-router'
import { CreateRouterOptions } from '@/typings/common'
import { StorageService } from '@/helpers/storage.service'
import { lkStoreModule } from '@/store/modules/lk'
import { authStoreModule } from '@/views/Auth/store'
import createFormdataRoutes from './formdata/routes'

import * as TYPES from '@/helpers/enums/view-types'
import { LK_PUBLICATIONS_ROUTES as ROUTES } from './utils'


type Options = Required<CreateRouterOptions>
type PromiseVue = Promise<typeof import('*.vue')>

const beforeEnterGuard = (options: Options, to: Route, from: Route, next: NavigationGuardNext) => {
  const storeModule = lkStoreModule.context(options.store)
  const authModule = authStoreModule.context(options.store)

  if (to.name !== ROUTES.LAYOUT) {
    const isActive = StorageService.lkNewsButton.get() && authModule.getters.authorized

    if (isActive) {
      storeModule.actions.getBanners({ slug: to.params.topic_slug })
    }

    return next()
  }

  const { slug } = (
    storeModule.state.topics.find((_) => _.is_default_topic && _.publications_count > 0)
    || storeModule.state.topics[0]
    || {}
  )

  const location = {
    name: ROUTES.LIST,
    params: { topic_slug: slug },
  }

  return next(location)
}

const createRoutes = (options: Options): RouteConfig[] => [
  {
    path: 'publications',
    name: ROUTES.LAYOUT,
    async beforeEnter(to, from, next) {
      const { registerModule } = await import(
        /* webpackChunkName: "lk-publications.store" */
        './store'
      )

      registerModule(options)
      const storeModule = lkStoreModule.context(options.store)

      const promises = [
        storeModule.actions.getDefaultPublication().catch(() => ({})),
        storeModule.actions.getTopics().catch(() => ({})),
        // Убрали подгрузку количества дорожных карт для юзера на главной странице
        // storeModule.actions.getRoadmapsCount().catch(() => ({})),
        storeModule.actions.getMunMehCount().catch(() => ({})),
      ]

      await Promise.all(promises)

      const { default_publication } = storeModule.state

      if (!default_publication) {
        return beforeEnterGuard(options, to, from, next)
      }

      const location = {
        name: ROUTES.VIEW,
        params: {
          topic_slug: default_publication.topic_slug,
          slug: default_publication.slug,
        },
      }

      const { route } = options.router.resolve(location)
      if (route.fullPath === to.fullPath) return next()
      return next(location)
    },
    component: {
      name: 'ViewLKPublicationsLayout',
      beforeRouteUpdate(to, from, next) {
        beforeEnterGuard(options, to, from, next)
      },
      render: (h: CreateElement) => h(
        'keep-alive',
        {
          props: {
            include: [
              'LKPublicationsList',
              'LKPublicationsView',
            ],
          },
        },
        [h('router-view')],
      ),
    },
    children: [
      // Порядок важен !!!
      ...createFormdataRoutes(options),
      {
        path: ':topic_slug',
        name: ROUTES.LIST,
        props: true,
        component: (): PromiseVue => import(
          /* webpackChunkName: "lk-publications.list" */
          './LKPublicationsList.vue'
        ),
      },
      {
        path: 'slug/:slug',
        name: ROUTES.VIEW_ONCE,
        props: ({ params: p }: Route) => ({ ...p, viewType: TYPES.VIEW }),
        component: (): PromiseVue => import(
          /* webpackChunkName: "lk-publications.view" */
          './LKPublicationsView.vue'
        ),
      },
      {
        path: ':topic_slug/:slug',
        name: ROUTES.VIEW,
        props: ({ params: p }: Route) => ({ ...p, viewType: TYPES.VIEW }),
        component: (): PromiseVue => import(
          /* webpackChunkName: "lk-publications.view" */
          './LKPublicationsView.vue'
        ),
      },
    ],
  },
]

const createModule = (options: Options) => {
  const routes = createRoutes(options)
  return routes
}

export default createModule
